import React, { useEffect, useState } from "react";
import { Box, Button, FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as RightIcon,
  ArrowDownward as DownIcon,
  PlayArrow as StartIcon,
  North as HigherIcon,
  South as LowerIcon,
  Gavel as RulesIcon,
} from "@mui/icons-material";
import { HOL, holGhostBtnSx, holPulse } from "../../../UserSide/pages/holTheme";
import { CardImage, Eyebrow, StepShell, holGradientText, holStartSessionBtnSx } from "./holHostUi";

const Pill = ({ children, color, Icon }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.5,
      py: 0.75,
      borderRadius: "12px",
      fontFamily: HOL.fontDisplay,
      fontWeight: 900,
      fontSize: "0.85rem",
      letterSpacing: "0.06em",
      color: "#0a0a0a",
      bgcolor: color,
      boxShadow: `0 8px 22px ${color}44`,
    }}
  >
    {children}
    {Icon ? <Icon sx={{ fontSize: 18 }} /> : null}
  </Box>
);

const ResultTag = ({ label, color }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: "10px",
      fontFamily: HOL.fontDisplay,
      fontWeight: 900,
      fontSize: "0.8rem",
      letterSpacing: "0.06em",
      color,
      border: `1px solid ${color}88`,
      bgcolor: `${color}1f`,
    }}
  >
    {label}
  </Box>
);

const WALKTHROUGH = [
  {
    n: "01",
    title: "A card appears",
    visual: <CardImage suit="spade" fileValue="7" width={70} />,
    players: "Everyone sees the current card.",
    host: "Pick the hot seat, then press Start Round to flip the first card.",
  },
  {
    n: "02",
    title: "Make your prediction",
    visual: (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
        <Pill color={HOL.higher} Icon={HigherIcon}>HIGHER</Pill>
        <Typography sx={{ color: HOL.muted, fontSize: "0.72rem", fontWeight: 700 }}>or</Typography>
        <Pill color={HOL.lower} Icon={LowerIcon}>LOWER</Pill>
      </Box>
    ),
    players: "The hot seat calls Higher or Lower (or Tie).",
    host: "Record the call. It locks in on every screen.",
  },
  {
    n: "03",
    title: "Reveal",
    visual: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <CardImage width={52} />
        <RightIcon sx={{ color: HOL.gold }} />
        <CardImage suit="diamond" fileValue="king" width={52} />
      </Box>
    ),
    players: "The next card flips on every screen.",
    host: "Press Reveal when the room is ready.",
  },
  {
    n: "04",
    title: "Score / Continue",
    visual: (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, alignItems: "center" }}>
        <ResultTag label="CORRECT +1" color={HOL.higher} />
        <ResultTag label="TIE +2" color={HOL.gold} />
        <ResultTag label="MISS" color={HOL.lower} />
      </Box>
    ),
    players: "See whether the call was right.",
    host: "Keep recording calls until the deck runs out, then pick the next hot seat.",
  },
];

const HolHowToPlayStep = ({ numberOfCards, onBack, onStartSession, busy = false, waiting = false }) => {
  const calls = Math.max(1, numberOfCards - 1);
  const [clueMax, setClueMax] = useState(calls);
  const [hideUserClue, setHideUserClue] = useState(true);

  useEffect(() => {
    setClueMax(calls);
  }, [calls]);
  const rules = [
    ["Correct Higher or Lower", "+1 point"],
    ["Correct Tie", "+2 points"],
    ["Same value, other call", "Push: no point, no penalty"],
    ["Wrong call", "No point. The run continues to the last card"],
    ["Deck", `${numberOfCards} cards per run · ${calls} calls`],
    ["Card order", "2 → 10 · J · Q · K · A · Joker (highest)"],
  ];

  return (
    <StepShell maxWidth={1100}>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
          <Eyebrow sx={{ mb: 1 }}>{waiting ? "Play with Host" : "Step 2 · Host briefing"}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              fontFamily: HOL.fontDisplay,
              fontWeight: 900,
              letterSpacing: "-0.01em",
              fontSize: { xs: "1.8rem", sm: "2.4rem", md: "2.8rem" },
              lineHeight: 1.05,
              ...holGradientText,
            }}
          >
            HOW ARE WE GOING TO PLAY?
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            gap: { xs: 0.5, md: 1 },
            mb: { xs: 3, md: 4 },
          }}
        >
          {WALKTHROUGH.map((step, i) => (
            <React.Fragment key={step.n}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  p: { xs: 1.25, sm: 2 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontFamily: HOL.fontDisplay,
                      fontWeight: 900,
                      fontSize: "1.9rem",
                      lineHeight: 1,
                      color: "transparent",
                      WebkitTextStroke: `1.5px ${HOL.gold}`,
                    }}
                  >
                    {step.n}
                  </Typography>
                  <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 800, fontSize: "1.05rem" }}>
                    {step.title}
                  </Typography>
                </Box>
                <Box sx={{ minHeight: 112, display: "grid", placeItems: "center", mb: 1.5 }}>{step.visual}</Box>
                <Box sx={{ mt: "auto", display: "grid", gap: 0.75 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.82)", lineHeight: 1.45 }}>
                    <Box component="span" sx={{ color: HOL.muted, fontWeight: 800, fontSize: "0.7rem", letterSpacing: "0.12em", mr: 0.75 }}>
                      PLAYERS
                    </Box>
                    {step.players}
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.82)", lineHeight: 1.45 }}>
                    <Box component="span" sx={{ color: HOL.gold, fontWeight: 800, fontSize: "0.7rem", letterSpacing: "0.12em", mr: 0.75 }}>
                      {waiting ? "HOST" : "YOU"}
                    </Box>
                    {step.host}
                  </Typography>
                </Box>
              </Box>
              {i < WALKTHROUGH.length - 1 ? (
                <Box aria-hidden sx={{ display: "grid", placeItems: "center", color: HOL.gold, opacity: 0.7 }}>
                  <RightIcon sx={{ display: { xs: "none", md: "block" } }} />
                  <DownIcon sx={{ display: { xs: "block", md: "none" } }} />
                </Box>
              ) : null}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ mb: { xs: 3, md: 4 }, pt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <RulesIcon sx={{ color: HOL.gold, fontSize: 20 }} />
            <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 800, letterSpacing: "0.06em" }}>
              GAME RULES
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {rules.map(([label, value]) => (
              <Grid item xs={12} sm={6} key={label}>
                <Box sx={{ display: "flex", gap: 1.25, alignItems: "baseline" }}>
                  <Typography sx={{ minWidth: 104, color: HOL.muted, fontSize: "0.82rem", fontWeight: 700 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {!waiting ? (
          <Box sx={{ mb: { xs: 3, md: 4 }, display: "grid", gap: 1.5, maxWidth: 520 }}>
            <Typography sx={{ fontFamily: HOL.fontDisplay, fontWeight: 800, letterSpacing: "0.06em" }}>
              CLUES
            </Typography>
            <TextField
              select
              size="small"
              label="Clues per player"
              value={clueMax}
              onChange={(e) => setClueMax(parseInt(e.target.value, 10))}
              helperText={`${numberOfCards} cards means up to ${calls} clues, one for each call.`}
            >
              {Array.from({ length: calls + 1 }, (_, n) => (
                <MenuItem key={n} value={n}>
                  {n === 0 ? "No clues" : n === calls ? `${n} — every call` : String(n)}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={hideUserClue}
                  onChange={(e) => setHideUserClue(e.target.checked)}
                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: HOL.gold } }}
                />
              }
              label="Hide the clue button on player phones"
            />
            <Typography sx={{ color: HOL.muted, fontSize: "0.82rem" }}>
              Leave this off and players see a locked clue button. Unlock it during the game before anyone, including you, can play it.
            </Typography>
          </Box>
        ) : null}

        {waiting ? (
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: HOL.fontDisplay,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: HOL.gold,
              animation: `${holPulse} 2.2s ease-in-out infinite`,
            }}
          >
            Waiting for the host to start the session
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Button variant="outlined" startIcon={<BackIcon />} onClick={onBack} disabled={busy} sx={{ ...holGhostBtnSx, px: 3, py: 1.1 }}>
              Back
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Typography sx={{ color: HOL.muted, fontSize: "0.82rem", maxWidth: 320, textAlign: "right" }}>
                This creates the live session and opens the join queue. Players on Higher or Lower join automatically.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<StartIcon />}
                onClick={() =>
                onStartSession({
                  clue_max_plays: clueMax,
                  clue_user_can_play: !hideUserClue,
                })
              }
                disabled={busy}
                autoFocus
                sx={holStartSessionBtnSx}
              >
                Start Session
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </StepShell>
  );
};

export default HolHowToPlayStep;
